import { de } from '../../../lib/i18n/de';
import { RESOURCES_SUB_STEPS, type ResourcesSubStepKey } from '../../../lib/store/wizard';

interface ResourcesSubStepNavProps {
  active: ResourcesSubStepKey;
  onSelect: (sub: ResourcesSubStepKey) => void;
}

/**
 * Segmentierter Indikator für die fünf Resources-Sub-Steps (4.1 – 4.5).
 *
 * Optisch reduziert auf Stone-Skala — die Wertigkeit der Sub-Steps liegt im
 * Inhalt, nicht im Indicator. Aktiver Sub-Step in Stone-900-Black, andere
 * Stone-300. Klein gehalten, damit der eigentliche Sub-Step-Inhalt im Fokus bleibt.
 */
export function ResourcesSubStepNav({ active, onSelect }: ResourcesSubStepNavProps) {
  const labels: Record<ResourcesSubStepKey, { short: string; name: string }> = {
    bwa: de.steps.resources.subSteps.bwa,
    rate: de.steps.resources.subSteps.rate,
    liquidity: de.steps.resources.subSteps.liquidity,
    bg: de.steps.resources.subSteps.bg,
    luecke: de.steps.resources.subSteps.luecke,
  };

  return (
    <nav
      aria-label="Sub-Schritte Modul 05"
      className="flex flex-wrap items-stretch gap-x-1 gap-y-3 border-b border-stone-200 pb-5"
    >
      {RESOURCES_SUB_STEPS.map((key, idx) => {
        const isActive = key === active;
        const label = labels[key];
        return (
          <button
            key={key}
            type="button"
            onClick={() => {
              onSelect(key);
            }}
            aria-current={isActive ? 'step' : undefined}
            className={`group flex flex-col items-start gap-1 rounded-sm px-3 py-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2 ${
              isActive ? 'bg-stone-900 text-white' : 'text-stone-500 hover:bg-stone-100'
            }`}
          >
            <span
              className={`font-sans text-[10px] font-medium uppercase tracking-widest ${
                isActive ? 'text-stone-300' : 'text-stone-500'
              }`}
            >
              {label.short}
            </span>
            <span
              className={`font-serif text-sm font-bold leading-tight tracking-tight ${
                isActive ? 'text-white' : 'text-stone-700 group-hover:text-stone-900'
              }`}
            >
              {label.name}
            </span>
            {idx < RESOURCES_SUB_STEPS.length - 1 && (
              <span aria-hidden="true" className="sr-only">
                ›
              </span>
            )}
          </button>
        );
      })}
    </nav>
  );
}
